'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  ACCOUNTING_PRICING_ROWS,
  isPricingHeader,
  pickPricingLocale,
  type PricingRow,
} from '@/data/accountingPricing';
import { useLanguage } from '@/contexts/LanguageContext';

type TFn = (key: string) => string;

interface AccountingPricingTableProps {
  t: TFn;
  /** Home page: collapsed by default. Modal: open by default. */
  defaultOpen?: boolean;
  showToggle?: boolean;
  /** Slightly tighter padding in modal */
  compact?: boolean;
  /** Less margin above the block (e.g. directly under section heading) */
  tightTop?: boolean;
  /** Narrow client modal: hide “online” column, min-width scroll, tighter text */
  embeddedInModal?: boolean;
}

export default function AccountingPricingTable({
  t,
  defaultOpen = false,
  showToggle = true,
  compact = false,
  tightTop = false,
  embeddedInModal = false,
}: AccountingPricingTableProps) {
  const [open, setOpen] = useState(defaultOpen);
  const { lang } = useLanguage();

  const modal = embeddedInModal;
  const hideOnline = modal;
  const colCount = hideOnline ? 4 : 5;
  const pad = modal ? 'px-4 py-2.5' : compact ? 'px-3 py-2' : 'px-5 py-3';
  const padSm = modal ? 'px-4 py-2.5' : compact ? 'px-3 py-2' : 'px-5 py-3';
  const bodyText = modal ? 'text-sm leading-relaxed sm:text-[15px]' : compact ? 'text-xs' : 'text-sm';
  const sectionText = modal ? 'px-4 py-2.5 text-xs sm:text-sm' : compact ? 'px-3 py-2 text-[10px]' : 'px-5 py-2.5 text-xs';
  /** Modal: ~30% wider columns vs previous so usluga/cene read clearly; horizontal scroll if needed */
  const modalThService =
    'min-w-[18rem] max-w-[29rem] text-left align-bottom font-semibold whitespace-normal';
  const modalThMoney = 'min-w-[10rem] whitespace-nowrap text-right align-bottom font-semibold';
  const modalThUnit = 'min-w-[12rem] whitespace-normal text-right align-bottom font-semibold leading-tight';
  const modalTdService = 'min-w-[18rem] max-w-[29rem] break-words [word-break:break-word]';
  const modalTdMoney = 'min-w-[10rem] whitespace-nowrap tabular-nums text-right';
  const modalTdUnit = 'min-w-[12rem] text-right align-top text-slate-500 dark:text-slate-400';

  const table = (
    <div
      className={`rounded-2xl border border-slate-200 overflow-hidden dark:border-slate-600 ${modal ? 'shadow-sm' : ''} ${compact || modal ? 'text-xs' : ''}`}
    >
      <div className={`overflow-x-auto overscroll-x-contain ${modal ? 'rounded-t-2xl' : ''}`}>
        <table
          className={`border-separate border-spacing-0 ${bodyText} ${modal ? 'w-max min-w-[min(100%,52rem)] table-auto sm:min-w-[56rem]' : 'w-full'}`}
        >
          <thead>
            <tr className="bg-[#1B3A5C] text-white">
              <th className={`${pad} ${modal ? modalThService : 'text-left font-semibold'}`}>
                {t('acc.pricing.service')}
              </th>
              <th className={`${padSm} ${modal ? modalThMoney : 'text-right font-semibold whitespace-nowrap'}`}>
                {t('acc.pricing.clientPrice')} (RSD)
              </th>
              <th className={`${padSm} ${modal ? modalThMoney : 'text-right font-semibold whitespace-nowrap'}`}>
                {t('acc.pricing.regularPrice')} (RSD)
              </th>
              <th className={`${padSm} ${modal ? modalThUnit : 'text-right font-semibold'}`}>{t('acc.pricing.unit')}</th>
              {!hideOnline ? (
                <th className={`text-center font-semibold ${compact ? 'px-2 py-2' : 'px-4 py-3'}`}>
                  {t('acc.pricing.online')}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {ACCOUNTING_PRICING_ROWS.map((row, idx) => {
              if (isPricingHeader(row)) {
                return (
                  <tr key={`ph-${idx}`} className="bg-slate-50 border-t border-slate-200 dark:bg-slate-800/40">
                    <td colSpan={colCount} className={`font-bold text-[#1B3A5C] uppercase tracking-wider dark:text-sky-200 ${sectionText}`}>
                      {pickPricingLocale(lang, {
                        sr: row.sr,
                        en: row.en,
                        de: row.de,
                        ru: row.ru,
                        tr: row.tr,
                      })}
                    </td>
                  </tr>
                );
              }
              const r = row as PricingRow;
              return (
                <tr key={`pr-${idx}`} className="border-t border-slate-100 hover:bg-slate-50/60 dark:border-slate-700/80 dark:hover:bg-slate-800/50 transition-colors">
                  <td className={`align-top text-slate-700 dark:text-slate-200 ${pad} ${modal ? modalTdService : 'leading-snug'}`}>
                    {pickPricingLocale(lang, r.service)}
                  </td>
                  <td
                    className={`text-right font-semibold text-[#1B3A5C] dark:text-sky-200 ${padSm} ${modal ? modalTdMoney : 'tabular-nums whitespace-nowrap'}`}
                  >
                    {r.client}
                  </td>
                  <td
                    className={`text-right font-medium text-slate-600 dark:text-slate-300 ${padSm} ${modal ? modalTdMoney : 'tabular-nums whitespace-nowrap'}`}
                  >
                    {r.regular}
                  </td>
                  <td className={`${padSm} ${modal ? `${modalTdUnit} whitespace-normal` : 'text-right text-slate-500 whitespace-pre-wrap dark:text-slate-400'}`}>
                    {pickPricingLocale(lang, r.unit)}
                  </td>
                  {!hideOnline ? (
                    <td className={`text-center ${compact ? 'px-2 py-2' : 'px-4 py-3'}`}>{r.online ? '✓' : '-'}</td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={`bg-slate-50 border-t border-slate-200 dark:border-slate-600 dark:bg-slate-800/50 ${modal ? 'px-4 py-2.5' : compact ? 'px-3 py-2' : 'px-5 py-3'}`}>
        <p className={`text-slate-400 dark:text-slate-500 ${modal ? 'text-xs sm:text-sm' : 'text-xs'}`}>{t('acc.pricing.footer')}</p>
      </div>
    </div>
  );

  if (!showToggle) {
    return <div className="mt-0">{table}</div>;
  }

  return (
    <div className={tightTop || embeddedInModal ? 'mt-0' : compact ? 'mt-2' : 'mt-16'}>
      <div
        className={`rounded-2xl border-2 border-[#1B3A5C]/15 bg-gradient-to-br from-slate-50 to-[#1B3A5C]/[0.04] shadow-sm hover:border-[#1B3A5C]/30 transition-colors dark:border-sky-400/20 dark:from-slate-800/80 dark:to-slate-900/80 ${
          embeddedInModal ? 'mb-2 p-3 sm:p-4' : compact ? 'mb-3 p-4' : 'mb-6 p-5 sm:p-6'
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className={`flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between w-full text-left cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1B3A5C]/40 ${embeddedInModal ? 'gap-3' : 'gap-4'}`}
        >
          <div className="min-w-0 pr-2">
            <p className="text-xs font-semibold text-[#C9A96E] uppercase tracking-widest mb-1">{t('acc.pricing.title')}</p>
            <p
              className={`text-slate-600 dark:text-slate-400 leading-snug ${embeddedInModal ? 'text-xs line-clamp-2 sm:line-clamp-none' : 'text-sm'}`}
            >
              {t('acc.pricing.subtitle')}
            </p>
          </div>
          <span
            className={`inline-flex items-center justify-center gap-2 rounded-xl border-2 border-[#1B3A5C]/20 bg-white text-sm font-semibold text-[#1B3A5C] shadow-sm group-hover:bg-[#1B3A5C]/[0.06] shrink-0 w-full sm:w-auto dark:bg-slate-800 dark:text-sky-200 dark:border-sky-400/30 pointer-events-none ${embeddedInModal ? 'px-3 py-2 text-xs sm:text-sm' : 'px-4 py-2.5'}`}
          >
            {open ? (
              <>
                <ChevronUp size={15} /> {t('acc.pricing.hide')}
              </>
            ) : (
              <>
                <ChevronDown size={15} /> {t('acc.pricing.show')}
              </>
            )}
          </span>
        </button>
      </div>
      {open && table}
    </div>
  );
}
